import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, Image as ImageIcon } from "lucide-react";
import { formatTimestamp, parseMediaFragmentSeconds } from "@/lib/composer/excerptDropFormat";
import { highlightHtml, highlightText } from "@/lib/highlight";
import { getSourceTypeIcon } from "@/lib/sourceTypeIcons";
import { getSourceRowToneVars } from "@/lib/sourceTypeRowTone";
import { getLibrarySourceTypeLabel, type LibrarySourceType } from "@/lib/library";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import type { LibraryCategory } from "@/lib/libraryCategories";

export type ExcerptRow = {
  id: string;
  body_html: string;
  body_text: string;
  source_url?: string | null;
  created_at: string;
  library_item_id?: string | null;
  library_items?:
    | {
        id: string;
        title: string;
        subtitle: string | null;
        display_title: string | null;
        author_text: string | null;
        published_year?: number | null;
        source_type?: LibrarySourceType | null;
        container_title?: string | null;
        accessed_date?: string | null;
        library_item_categories?: { library_categories: LibraryCategory | null }[] | null;
      }
    | null;
  excerpt_tags?: { tags: { name: string } | null }[] | null;
  excerpt_topics?: { content_framework_topics: { label: string } | null }[] | null;
};

type Props = {
  excerpt: ExcerptRow;
  selectMode: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onDuplicate: (e: React.MouseEvent, it: ExcerptRow) => void;
  onSendToImageStudio: (e: React.MouseEvent, it: ExcerptRow) => void;
  onChipClick?: (kind: "tag" | "topic", name: string) => void;
  highlightTokens?: string[];
  /** Optional total count of token hits in body, shown as a small badge. */
  matchCount?: number;
  /** When > 0, indicates matches came (also) from the notes field. */
  notesMatchCount?: number;
  /** Search params (q/tag/topic) to forward to the detail page so Back can return here. */
  forwardParams?: URLSearchParams;
  /** Called just before navigating to the detail page (e.g. to save scroll position). */
  onNavigateToDetail?: () => void;
};

export const ExcerptCard = ({
  excerpt,
  selectMode,
  isSelected,
  onToggleSelect,
  onDuplicate,
  onSendToImageStudio,
  onChipClick,
  highlightTokens,
  matchCount,
  notesMatchCount,
  forwardParams,
  onNavigateToDetail,
}: Props) => {
  const handleChipClick = (
    e: React.MouseEvent,
    kind: "tag" | "topic",
    name: string
  ) => {
    if (!onChipClick) return;
    e.preventDefault();
    e.stopPropagation();
    onChipClick(kind, name);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tokens = highlightTokens ?? [];
  const bodyHtml = useMemo(
    () => highlightHtml(excerpt.body_html, tokens),
    [excerpt.body_html, tokens]
  );
  const authorName = excerpt.library_items?.author_text ?? "";
  const containerTitle = excerpt.library_items?.container_title ?? "";
  const episodeOrPublication =
    excerpt.library_items?.display_title ?? excerpt.library_items?.title ?? "";
  const sourceType = excerpt.library_items?.source_type ?? null;
  const year = excerpt.library_items?.published_year ?? null;
  const isPodcast = sourceType === "podcast" && !!containerTitle;
  // For podcasts, the "publication" line shows the show as the work-of-record
  // and the episode title is added as a secondary chip; for everything else,
  // the existing single "publication" label is preserved.
  const publicationName = isPodcast ? containerTitle : episodeOrPublication;
  const timestampStr = formatTimestamp(parseMediaFragmentSeconds(excerpt.source_url));
  const authorHtml = useMemo(
    () => (authorName ? highlightText(authorName, tokens) : ""),
    [authorName, tokens]
  );
  const publicationHtml = useMemo(
    () => (publicationName ? highlightText(publicationName, tokens) : ""),
    [publicationName, tokens]
  );
  const episodeHtml = useMemo(
    () => (isPodcast && episodeOrPublication ? highlightText(episodeOrPublication, tokens) : ""),
    [isPodcast, episodeOrPublication, tokens]
  );

  const inheritedCategories: LibraryCategory[] = useMemo(() => {
    const raw = excerpt.library_items?.library_item_categories ?? [];
    return raw
      .map((row) => row.library_categories)
      .filter((c): c is LibraryCategory => Boolean(c))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [excerpt.library_items?.library_item_categories]);

  const tone = getSourceRowToneVars(sourceType);
  const [hovered, setHovered] = useState(false);

  const inner = (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? tone.hover : tone.idle,
        borderLeft: `4px solid ${tone.accent}`,
      }}
      className={`shadow-soft hover:shadow-elevated transition-shadow ${
        selectMode && isSelected ? "ring-2 ring-primary" : ""
      }`}
    >
      <CardContent className="py-5 space-y-3">
        <div className="flex items-start justify-between gap-4">
          {selectMode && (
            <Checkbox
              checked={isSelected}
              className="mt-1 shrink-0"
              onCheckedChange={() => onToggleSelect(excerpt.id)}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div
            className="text-lg font-semibold leading-snug line-clamp-3 flex-1"
            style={{ color: tone.title }}
            dir="auto"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          {!selectMode && (
            <div className="hidden md:flex shrink-0 gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => onDuplicate(e, excerpt)}
                title="Duplicate"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => onSendToImageStudio(e, excerpt)}
                title="Send to Image Studio"
              >
                <ImageIcon className="h-4 w-4" /> Make image
              </Button>
            </div>
          )}
        </div>
        {inheritedCategories.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {inheritedCategories.map((cat) => (
              <LibraryCategoryBadge key={cat.id} category={cat} />
            ))}
          </div>
        ) : null}
        <div className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
          {(() => {
            if (!sourceType) return null;
            const Icon = getSourceTypeIcon(sourceType);
            return (
              <Badge
                variant="outline"
                className="gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                style={{ color: tone.title, borderColor: tone.selected, backgroundColor: "transparent" }}
              >
                <Icon className="h-3 w-3" />
                {getLibrarySourceTypeLabel(sourceType)}
              </Badge>
            );
          })()}
          {authorName && (
            <span
              className="font-semibold"
              style={{ color: tone.title }}
              dangerouslySetInnerHTML={{ __html: authorHtml }}
            />
          )}
          {publicationName && (
            <span>
              {authorName && "· "}
              <em dangerouslySetInnerHTML={{ __html: publicationHtml }} />
            </span>
          )}
          {isPodcast && episodeOrPublication && (
            <span>
              · <span dangerouslySetInnerHTML={{ __html: episodeHtml }} />
            </span>
          )}
          {isPodcast && year && <span>· {year}</span>}
          {isPodcast && timestampStr && <span>· {timestampStr}</span>}
          {matchCount && matchCount > 0 ? (
            <span className="basis-full sm:basis-auto sm:ml-auto inline-flex items-center gap-1.5">
              {notesMatchCount && notesMatchCount > 0 ? (
                <span
                  className="text-[10px] uppercase tracking-wide rounded-full border border-sage/40 bg-sage/20 text-foreground px-2 py-0.5"
                  title={`${notesMatchCount} of ${matchCount} match${matchCount === 1 ? "" : "es"} in notes`}
                >
                  in notes
                </span>
              ) : null}
              <span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5">
                {matchCount} {matchCount === 1 ? "match" : "matches"}
              </span>
            </span>
          ) : null}
        </div>
        {(() => {
          const tagNames = (excerpt.excerpt_tags ?? [])
            .map((et) => et.tags?.name)
            .filter((n): n is string => Boolean(n));
          const topicNames = (excerpt.excerpt_topics ?? [])
            .map((et) => et.content_framework_topics?.label)
            .filter((n): n is string => Boolean(n));
          if (!tagNames.length && !topicNames.length) return null;
          return (
            <div className="flex flex-wrap gap-1.5">
              {topicNames.map((c) => (
                <Badge
                  key={`tp-${c}`}
                  variant="outline"
                  onClick={(e) => handleChipClick(e, "topic", c)}
                  className={`text-xs border-primary text-primary bg-primary/10 hover:bg-primary/20 ${
                    onChipClick ? "cursor-pointer" : ""
                  }`}
                >
                  {c}
                </Badge>
              ))}
              {tagNames.map((t) => (
                <Badge
                  key={`t-${t}`}
                  variant="outline"
                  onClick={(e) => handleChipClick(e, "tag", t)}
                  className={`text-xs border-sage/50 text-foreground bg-sage/15 hover:bg-sage/25 ${
                    onChipClick ? "cursor-pointer" : ""
                  }`}
                >
                  {t}
                </Badge>
              ))}
            </div>
          );
        })()}
        {!selectMode && (
          <div className="flex md:hidden gap-1 pt-1 -mb-1 border-t border-border/50 -mx-1 px-1 mt-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-9 flex-1 justify-center text-xs"
              onClick={(e) => onDuplicate(e, excerpt)}
            >
              <Copy className="h-4 w-4 mr-1" /> Duplicate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 flex-1 justify-center text-xs"
              onClick={(e) => onSendToImageStudio(e, excerpt)}
            >
              <ImageIcon className="h-4 w-4 mr-1" /> Make image
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (selectMode) {
    return (
      <div className="cursor-pointer" onClick={() => onToggleSelect(excerpt.id)}>
        {inner}
      </div>
    );
  }
  const forwarded = (() => {
    if (!forwardParams) return "";
    const next = new URLSearchParams();
    const q = forwardParams.get("q");
    const tag = forwardParams.get("tag");
    const topic = forwardParams.get("topic");
    const libraryItem = forwardParams.get("libraryItem");
    const returnTo = forwardParams.get("returnTo");
    if (q) next.set("q", q);
    if (tag) next.set("tag", tag);
    if (topic) next.set("topic", topic);
    if (libraryItem) next.set("libraryItem", libraryItem);
    if (returnTo) next.set("returnTo", returnTo);
    const s = next.toString();
    return s ? `?${s}` : "";
  })();
  const detailHref = `/excerpts/${excerpt.id}${forwarded}`;
  return (
    <Link to={detailHref} className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl block" data-excerpt-card data-excerpt-id={excerpt.id} onClick={() => onNavigateToDetail?.()}>
      {inner}
    </Link>
  );
};
